import React, { useState, useEffect } from 'react';
import { debtPayoffService } from '../../../services/debtPayoffService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PaymentHistoryModal = ({ debt, onClose }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (debt?.id) {
      loadPaymentHistory();
    }
  }, [debt?.id]);

  const loadPaymentHistory = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await debtPayoffService?.getPaymentHistory(debt?.id);
      setPayments(data);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-16">
      <div className="bg-card rounded-lg shadow-elevation-4 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-24 border-b border-border sticky top-0 bg-card z-10">
          <div>
            <h2 className="text-xl font-semibold text-card-foreground">Payment History</h2>
            <p className="text-sm text-muted-foreground mt-4">{debt?.debtName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-8 hover:bg-muted rounded-lg transition-smooth min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="p-24">
          {loading && (
            <div className="text-center py-48">
              <Icon name="Loader2" size={32} color="var(--color-primary)" className="animate-spin mx-auto mb-12" />
              <p className="text-muted-foreground">Loading payment history...</p>
            </div>
          )}

          {error && (
            <div className="p-12 bg-destructive/10 border border-destructive rounded-lg mb-16">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {!loading && !error && payments?.length === 0 && (
            <div className="text-center py-48">
              <Icon name="Calendar" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-12" />
              <p className="text-muted-foreground">No payment history yet</p>
              <p className="text-sm text-muted-foreground mt-8">Payments will appear here automatically when transactions are made to the linked account</p>
            </div>
          )}

          {!loading && !error && payments?.length > 0 && (
            <>
              {/* Desktop view */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-16 py-12 text-left text-sm font-semibold text-foreground">Date</th>
                      <th className="px-16 py-12 text-right text-sm font-semibold text-foreground">Payment</th>
                      <th className="px-16 py-12 text-right text-sm font-semibold text-foreground">Principal</th>
                      <th className="px-16 py-12 text-right text-sm font-semibold text-foreground">Interest</th>
                      <th className="px-16 py-12 text-right text-sm font-semibold text-foreground">Balance</th>
                      <th className="px-16 py-12 text-left text-sm font-semibold text-foreground">Source</th>
                      <th className="px-16 py-12 text-left text-sm font-semibold text-foreground">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {payments?.map((payment) => (
                      <tr key={payment?.id} className="hover:bg-muted/50 transition-smooth">
                        <td className="px-16 py-16 text-sm text-foreground">
                          {new Date(payment?.paymentDate)?.toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </td>
                        <td className="px-16 py-16 text-sm font-semibold text-right text-foreground">
                          ${payment?.paymentAmount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-16 py-16 text-sm text-right text-success">
                          ${payment?.principalPaid?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-16 py-16 text-sm text-right text-muted-foreground">
                          ${payment?.interestPaid?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-16 py-16 text-sm text-right font-medium text-foreground">
                          ${payment?.remainingBalance?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-16 py-16 text-sm">
                          {payment?.isAutomatic ? (
                            <div className="flex items-center gap-8">
                              <Icon name="Zap" size={16} color="var(--color-primary)" />
                              <span className="text-primary font-medium">Auto</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-8">
                              <Icon name="User" size={16} color="var(--color-muted-foreground)" />
                              <span className="text-muted-foreground">Manual</span>
                            </div>
                          )}
                        </td>
                        <td className="px-16 py-16 text-sm text-muted-foreground max-w-xs truncate">
                          {payment?.transaction ? (
                            <div>
                              <div className="font-medium text-foreground">{payment?.transaction?.description}</div>
                              <div className="text-xs text-muted-foreground mt-4">From transaction</div>
                            </div>
                          ) : (
                            payment?.notes || '-'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile view - stacked cards */}
              <div className="md:hidden space-y-12">
                {payments?.map((payment) => (
                  <div key={payment?.id} className="bg-muted/30 rounded-lg p-16 border border-border">
                    <div className="flex items-start justify-between mb-12">
                      <div className="flex-1">
                        <div className="text-sm text-muted-foreground mb-4">
                          {new Date(payment?.paymentDate)?.toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </div>
                        <div className="text-lg font-semibold text-foreground">
                          ${payment?.paymentAmount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                      <div>
                        {payment?.isAutomatic ? (
                          <div className="flex items-center gap-6 px-10 py-6 bg-primary/10 rounded-lg">
                            <Icon name="Zap" size={14} color="var(--color-primary)" />
                            <span className="text-xs text-primary font-medium">Auto</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-6 px-10 py-6 bg-muted rounded-lg">
                            <Icon name="User" size={14} color="var(--color-muted-foreground)" />
                            <span className="text-xs text-muted-foreground font-medium">Manual</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-12 mb-12">
                      <div>
                        <div className="text-xs text-muted-foreground mb-4">Principal</div>
                        <div className="text-sm font-medium text-success">
                          ${payment?.principalPaid?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground mb-4">Interest</div>
                        <div className="text-sm font-medium text-muted-foreground">
                          ${payment?.interestPaid?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                    </div>

                    <div className="mb-12">
                      <div className="text-xs text-muted-foreground mb-4">Remaining Balance</div>
                      <div className="text-base font-semibold text-foreground">
                        ${payment?.remainingBalance?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>

                    {(payment?.transaction || payment?.notes) && (
                      <div className="pt-12 border-t border-border">
                        <div className="text-xs text-muted-foreground mb-4">Notes</div>
                        {payment?.transaction ? (
                          <div>
                            <div className="text-sm font-medium text-foreground">{payment?.transaction?.description}</div>
                            <div className="text-xs text-muted-foreground mt-4">From transaction</div>
                          </div>
                        ) : (
                          <div className="text-sm text-muted-foreground">{payment?.notes}</div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-12 p-24 border-t border-border sticky bottom-0 bg-card">
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentHistoryModal;